import React, { useState, useRef, useEffect } from 'react';
import { FormControl } from 'baseui/form-control';
import { Input } from 'baseui/input';
import { Checkbox, LABEL_PLACEMENT } from 'baseui/checkbox';
import { ParagraphSmall } from 'baseui/typography';
import { ChevronDown, ChevronRight, Plus, X, Copy } from 'lucide-react';
import { S3ImageUpload } from '../S3ImageUpload';
import {
  DAYS,
  type DayKey,
  type ProductConfigForm as ConfigForm,
  type TierForm,
  type BlackoutForm,
} from './productConfig';
import { DateField } from '../ui/DateTimeField';

const DEFAULT_RANGE = { open: '09:00', close: '18:00' };

// Native time input that stays uncontrolled while the user types (so a parent
// re-render never wipes a half-entered "HH:--" value), but still reflects
// external changes (loading a product, "copy day to all") when not focused.
// Replaces BaseUI's <Input type="time">, whose wrapper reset the native field's
// segment editing mid-typing — the value never stuck.
const TimeField: React.FC<{ value: string; onChange: (v: string) => void; ariaLabel?: string }> = ({ value, onChange, ariaLabel }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.value !== value) el.value = value;
  }, [value]);
  return (
    <input
      ref={ref}
      type="time"
      className="bz-time-input"
      aria-label={ariaLabel}
      defaultValue={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

interface Props {
  value: ConfigForm;
  onChange: (next: ConfigForm) => void;
}

const Section: React.FC<{ title: string; sub?: string; open: boolean; onToggle: () => void; children: React.ReactNode }> = ({ title, sub, open, onToggle, children }) => (
  <section className="bz-section">
    <button
      type="button"
      onClick={onToggle}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
    >
      {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      <span className="bz-section-title" style={{ marginBottom: 0 }}>{title}</span>
    </button>
    {open && (
      <div style={{ marginTop: 12 }}>
        {sub && <ParagraphSmall margin="0 0 12px" color="#6B7280" style={{ fontSize: 13 }}>{sub}</ParagraphSmall>}
        {children}
      </div>
    )}
  </section>
);

const numInput = (val: string, onVal: (v: string) => void, placeholder: string, enhancer?: string) => (
  <Input value={val} onChange={(e) => onVal((e.target as HTMLInputElement).value)} placeholder={placeholder} type="number" size="compact" startEnhancer={enhancer} />
);

export const ProductConfigForm: React.FC<Props> = ({ value, onChange }) => {
  const [openSec, setOpenSec] = useState<Record<string, boolean>>({ schedule: true, slots: false, capacity: false, restrictions: false, tiers: false, images: false, categories: false });
  const toggle = (k: string) => setOpenSec((p) => ({ ...p, [k]: !p[k] }));
  const set = (patch: Partial<ConfigForm>) => onChange({ ...value, ...patch });

  // ── Schedule helpers ──
  const setDayClosed = (key: DayKey, closed: boolean) =>
    set({ schedule: { ...value.schedule, [key]: { ...value.schedule[key], closed } } });
  const setRange = (key: DayKey, idx: number, field: 'open' | 'close', v: string) => {
    const ranges = value.schedule[key].ranges.map((r, i) => (i === idx ? { ...r, [field]: v } : r));
    set({ schedule: { ...value.schedule, [key]: { ...value.schedule[key], ranges } } });
  };
  const addRange = (key: DayKey) =>
    set({ schedule: { ...value.schedule, [key]: { ...value.schedule[key], ranges: [...value.schedule[key].ranges, { ...DEFAULT_RANGE }] } } });
  const removeRange = (key: DayKey, idx: number) => {
    const filtered = value.schedule[key].ranges.filter((_, i) => i !== idx);
    set({ schedule: { ...value.schedule, [key]: { ...value.schedule[key], ranges: filtered.length ? filtered : [{ ...DEFAULT_RANGE }] } } });
  };
  const copyDayToAll = (key: DayKey) => {
    const src = value.schedule[key];
    const next = { ...value.schedule };
    DAYS.forEach(({ key: k }) => { if (k !== key) next[k] = { closed: src.closed, ranges: src.ranges.map((r) => ({ ...r })) }; });
    set({ schedule: next });
  };

  // ── Tiers ──
  const addTier = () => set({ tiers: [...value.tiers, { id: '', label_en: '', label_es: '', price: '', currency: 'USD', min_amount: '', max_amount: '' }] });
  const updateTier = (i: number, field: keyof TierForm, v: string) => set({ tiers: value.tiers.map((t, idx) => (idx === i ? { ...t, [field]: v } : t)) });
  const removeTier = (i: number) => set({ tiers: value.tiers.filter((_, idx) => idx !== i) });

  // ── Blackouts ──
  const addBlackout = () => set({ blackouts: [...value.blackouts, { start: '', end: '', reason: '' }] });
  const updateBlackout = (i: number, field: keyof BlackoutForm, v: string) => set({ blackouts: value.blackouts.map((b, idx) => (idx === i ? { ...b, [field]: v } : b)) });
  const removeBlackout = (i: number) => set({ blackouts: value.blackouts.filter((_, idx) => idx !== i) });

  // ── Images ──
  const setImage = (i: number, key: string) => set({ images: value.images.map((im, idx) => (idx === i ? key : im)) });
  const addImage = (key: string) => { if (key) set({ images: [...value.images, key] }); };
  const removeImage = (i: number) => set({ images: value.images.filter((_, idx) => idx !== i) });

  // ── Categories ──
  const [catInput, setCatInput] = useState('');
  const addCategory = () => { const c = catInput.trim(); if (c && !value.categories.includes(c)) { set({ categories: [...value.categories, c] }); } setCatInput(''); };
  const removeCategory = (c: string) => set({ categories: value.categories.filter((x) => x !== c) });

  const iconBtn = (onClick: () => void, disabled = false) => (
    <button type="button" className="bk-icon-btn" onClick={onClick} disabled={disabled} style={{ opacity: disabled ? 0.35 : 1 }}>
      <X size={13} />
    </button>
  );

  return (
    <>
      <div className="bz-section-title" style={{ marginTop: 8, marginBottom: 4, color: '#111827', fontSize: 13 }}>
        Availability &amp; conditions
      </div>

      {/* Schedule */}
      <Section title="Availability schedule" sub="Per-day hours for this product. If disabled, the app uses the business schedule." open={openSec.schedule} onToggle={() => toggle('schedule')}>
        <Checkbox checked={value.enableSchedule} onChange={(e) => set({ enableSchedule: (e.target as HTMLInputElement).checked })} labelPlacement={LABEL_PLACEMENT.right}>
          Use a product-specific schedule
        </Checkbox>
        {value.enableSchedule && (
          <div style={{ marginTop: 12 }}>
            {DAYS.map(({ key, label }) => {
              const day = value.schedule[key];
              return (
                <div key={key} style={{ display: 'grid', gridTemplateColumns: '70px 90px 1fr 28px', gap: 10, alignItems: 'start', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontWeight: 500, fontSize: 13, paddingTop: 8 }}>{label}</span>
                  <div style={{ paddingTop: 6 }}>
                    <Checkbox checked={day.closed} onChange={(e) => setDayClosed(key, (e.target as HTMLInputElement).checked)} labelPlacement={LABEL_PLACEMENT.right}>Closed</Checkbox>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {day.closed ? (
                      <ParagraphSmall margin="6px 0 0" color="#9CA3AF" style={{ fontSize: 13 }}>Closed all day</ParagraphSmall>
                    ) : (
                      <>
                        {day.ranges.map((r, idx) => (
                          <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 28px', gap: 8, alignItems: 'center' }}>
                            <TimeField value={r.open} onChange={(v) => setRange(key, idx, 'open', v)} ariaLabel={`${label} open time`} />
                            <TimeField value={r.close} onChange={(v) => setRange(key, idx, 'close', v)} ariaLabel={`${label} close time`} />
                            {iconBtn(() => removeRange(key, idx), day.ranges.length <= 1)}
                          </div>
                        ))}
                        <button type="button" className="bz-secondary-btn" onClick={() => addRange(key)} style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 12 }}>
                          <Plus size={13} /> Add block
                        </button>
                      </>
                    )}
                  </div>
                  <button type="button" className="bz-copy-day-btn" onClick={() => copyDayToAll(key)} title={`Apply ${label} to all days`} style={{ marginTop: 4 }}>
                    <Copy size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Slots */}
      <Section title="Slots" sub="Reservation length and how often a slot is offered (minutes). Defaults to 30 if empty." open={openSec.slots} onToggle={() => toggle('slots')}>
        <div className="bz-grid-2">
          <FormControl label="Slot interval (min)">{numInput(value.slotInterval, (v) => set({ slotInterval: v }), 'e.g. 30')}</FormControl>
          <FormControl label="Reservation duration (min)">{numInput(value.slotDuration, (v) => set({ slotDuration: v }), 'e.g. 60')}</FormControl>
        </div>
      </Section>

      {/* Capacity */}
      <Section title="Capacity & party size" open={openSec.capacity} onToggle={() => toggle('capacity')}>
        <div className="bz-grid-2">
          <FormControl label="Max bookings per slot">{numInput(value.maxPerSlot, (v) => set({ maxPerSlot: v }), 'unlimited')}</FormControl>
          <FormControl label="Max people per day (aforo)">{numInput(value.maxCapacity, (v) => set({ maxCapacity: v }), 'unlimited')}</FormControl>
          <FormControl label="Min party size">{numInput(value.minParty, (v) => set({ minParty: v }), '1')}</FormControl>
          <FormControl label="Max party size">{numInput(value.maxParty, (v) => set({ maxParty: v }), 'unlimited')}</FormControl>
        </div>
      </Section>

      {/* Restrictions & window */}
      <Section title="Restrictions & booking window" open={openSec.restrictions} onToggle={() => toggle('restrictions')}>
        <div className="bz-grid-2">
          <FormControl label="Minimum age">{numInput(value.minAge, (v) => set({ minAge: v }), 'none')}</FormControl>
          <FormControl label=" ">
            <Checkbox checked={value.requiresId} onChange={(e) => set({ requiresId: (e.target as HTMLInputElement).checked })} labelPlacement={LABEL_PLACEMENT.right}>Requires ID</Checkbox>
          </FormControl>
          <FormControl label="Min advance (hours)">{numInput(value.minAdvanceHours, (v) => set({ minAdvanceHours: v }), 'e.g. 2')}</FormControl>
          <FormControl label="Max advance (days)">{numInput(value.maxAdvanceDays, (v) => set({ maxAdvanceDays: v }), 'e.g. 30')}</FormControl>
        </div>
        <div style={{ marginTop: 8 }}>
          <ParagraphSmall margin="0 0 8px" color="#6B7280" style={{ fontSize: 13 }}>Blackout dates (closed ranges)</ParagraphSmall>
          {value.blackouts.map((b, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 28px', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <DateField ariaLabel={`Blackout ${i + 1} start date`} placeholder="From" value={b.start} onChange={(v) => updateBlackout(i, 'start', v)} />
              <DateField ariaLabel={`Blackout ${i + 1} end date`} placeholder="To" value={b.end} onChange={(v) => updateBlackout(i, 'end', v)} />
              <Input value={b.reason} onChange={(e) => updateBlackout(i, 'reason', (e.target as HTMLInputElement).value)} placeholder="Reason" size="compact" />
              {iconBtn(() => removeBlackout(i))}
            </div>
          ))}
          <button type="button" className="bz-secondary-btn" onClick={addBlackout} style={{ padding: '4px 10px', fontSize: 12 }}><Plus size={13} /> Add blackout</button>
        </div>
      </Section>

      {/* Pricing tiers */}
      <Section title={`Pricing tiers${value.tiers.length ? ` (${value.tiers.length})` : ''}`} sub="Per-person-type pricing (e.g. Adults / Children). If empty, the base price applies to everyone." open={openSec.tiers} onToggle={() => toggle('tiers')}>
        {value.tiers.map((t, i) => (
          <div key={i} style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong style={{ fontSize: 13 }}>Tier {i + 1}</strong>
              {iconBtn(() => removeTier(i))}
            </div>
            <div className="bz-grid-2">
              <FormControl label="ID"><Input value={t.id} onChange={(e) => updateTier(i, 'id', (e.target as HTMLInputElement).value)} placeholder="adults" size="compact" /></FormControl>
              <FormControl label="Price">{numInput(t.price, (v) => updateTier(i, 'price', v), '0.00', '$')}</FormControl>
              <FormControl label="Label (EN)"><Input value={t.label_en} onChange={(e) => updateTier(i, 'label_en', (e.target as HTMLInputElement).value)} placeholder="Adults" size="compact" /></FormControl>
              <FormControl label="Label (ES)"><Input value={t.label_es} onChange={(e) => updateTier(i, 'label_es', (e.target as HTMLInputElement).value)} placeholder="Adultos" size="compact" /></FormControl>
              <FormControl label="Min qty">{numInput(t.min_amount, (v) => updateTier(i, 'min_amount', v), '0')}</FormControl>
              <FormControl label="Max qty">{numInput(t.max_amount, (v) => updateTier(i, 'max_amount', v), 'unlimited')}</FormControl>
            </div>
          </div>
        ))}
        <button type="button" className="bz-secondary-btn" onClick={addTier} style={{ padding: '4px 10px', fontSize: 12 }}><Plus size={13} /> Add tier</button>
      </Section>

      {/* Images */}
      <Section title={`Images${value.images.length ? ` (${value.images.length})` : ''}`} open={openSec.images} onToggle={() => toggle('images')}>
        {value.images.map((im, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <S3ImageUpload value={im} onChange={(key) => setImage(i, key)} label={`Image ${i + 1}`} previewHeight="120px" />
            </div>
            {iconBtn(() => removeImage(i))}
          </div>
        ))}
        <div style={{ marginTop: 4 }}>
          <S3ImageUpload value="" onChange={addImage} label="Add image" previewHeight="120px" />
        </div>
      </Section>

      {/* Categories */}
      <Section title={`Categories${value.categories.length ? ` (${value.categories.length})` : ''}`} open={openSec.categories} onToggle={() => toggle('categories')}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <Input value={catInput} onChange={(e) => setCatInput((e.target as HTMLInputElement).value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }} placeholder="Add a category and press Enter" size="compact" />
          <button type="button" className="bz-secondary-btn" onClick={addCategory} style={{ padding: '4px 12px', fontSize: 12 }}><Plus size={13} /> Add</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {value.categories.map((c) => (
            <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F3F4F6', borderRadius: 999, padding: '4px 10px', fontSize: 12 }}>
              {c}
              <button type="button" onClick={() => removeCategory(c)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}><X size={12} /></button>
            </span>
          ))}
        </div>
      </Section>
    </>
  );
};
